import { falBell } from 'shared/@generated/icons/falBell';
import React, { ReactNode, memo, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Platform, View } from 'react-native';
import { Listing, Paginator, Animal, Breed } from 'shared/@types';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'shared/components/link';
import { apiRoute } from 'shared/library/api-route';
import { ListingCard } from 'shared/components/listing-card';
import { Pagination } from 'shared/components/pagination';
import { Text } from 'shared/components/text';
import { H1, H2 } from 'shared/components/title';
import { Button } from 'shared/components/button';
import {
  getListingsFilterQuery,
  ListingsFilter,
  ListingsFilterProvider,
  sanitizeListingsFilterParams,
  useListingsFilterStore,
} from 'shared/state/listings-filter';

import { buildQuery, useDebouncedQueryFilter } from 'shared/library/use-debounced-query-filter';
import { prepareDataForApi } from 'shared/library/api-client';

import { SearchModal } from 'shared/components/search-modal';
import { PageBuilder } from 'shared/library/page-builder';
import { analytics } from 'shared/library/analytics';
import { PageContent } from 'shared/components/page-content';
import { Breadcrumb } from 'shared/components/breadcrumb';
import { useRoute } from 'shared/library/routes/route';
import { ProductFeedbackPrompt } from 'shared/components/product-feedback-prompt';
import {
  compactListingCardList,
  compactListingCardPaginator,
} from 'shared/library/compact-page-data';
import { ListingsWebFilterControls } from 'shared/components/listings-web-filter-controls';
import { AdBanner } from 'shared/components/ad-banner';
import { AdAnimalProvider } from 'shared/components/ad-banner/ad-animal-context';
import { resolveAdAnimal } from 'shared/components/ad-banner/ad-animal';
import useBreedsStore from 'shared/state/breeds';

interface IndexListingsScreenProps {
  initialListings: Paginator<Listing>;
  boostedListings: Listing[];
  totalPets: number;
  initialFilter: ListingsFilter;
  loadingInitial?: boolean;
  refreshing?: boolean;
  onRefresh?: () => void;
  header?: ReactNode;
  title?: string;
  subtitle?: string;
  onSelectBreed?: (breed: Breed, currentFilter?: ListingsFilter) => void;
  onSelectBreedQuery?: (value: string, currentFilter?: ListingsFilter) => void;
  onClearBreedSelection?: (currentFilter?: ListingsFilter) => void;
  queryPathname?: string;
  queryDefaults?: Partial<ListingsFilter>;
  querySyncEnabled?: boolean;
  onQueryChange?: (href: string) => void;
  initialBreeds?: Breed[];
  showAds?: boolean;
  noResultsTitle?: string;
  noResultsSubtitle?: string;
  fallbackListings?: Listing[];
  fallbackTitle?: string;
  fallbackSubtitle?: string;
}

type IndexListingItem = Listing & {
  boosted?: boolean;
  priorityImage?: boolean;
};

const listingPageTranslationPaths = [
  'common',
  'routes',
  'web.layout',
  'screens.login',
  'screens.listings',
  'library.use_formatter',
  'components.account_overview_menu',
  'components.account_verification_warning',
  'components.breeder_badge',
  'components.confirm_modal',
  'components.data_sharing_reprompt_modal',
  'components.form',
  'components.guest_promo_modal',
  'components.like_button',
  'components.listings_filter_form',
  'components.litter_insurance_badge',
  'components.locale_switcher',
  'components.onboarding',
  'components.pagination',
  'components.reviews_card',
  'components.search_modal',
];

const IndexListingsScreen = (props: IndexListingsScreenProps) => {
  // The applied route filter changes with the results, before the local filter store syncs.
  const adAnimal = useBreedsStore((state) => {
    if (props.initialFilter.animal) return props.initialFilter.animal;

    return resolveAdAnimal({
      breedIds: props.initialFilter.breeds,
      breeds: [...(props.initialBreeds ?? []), ...state.breeds],
    });
  });

  if (
    props.loadingInitial &&
    props.initialListings.data.length === 0 &&
    props.boostedListings.length === 0
  ) {
    return (
      <PageContent className={'flex-1'} contentClassName={'flex-1'} nativeSafeTop={true}>
        <View className={'flex-1 items-center justify-center py-20'}>
          <ActivityIndicator size={'large'} color={'#2b120d'} />
        </View>
      </PageContent>
    );
  }

  return (
    <AdAnimalProvider animal={adAnimal}>
      <ListingsFilterProvider initialState={props.initialFilter}>
        <ScreenContent {...props} />
      </ListingsFilterProvider>
    </AdAnimalProvider>
  );
};

const ScreenContent = memo(function ScreenContent(props: IndexListingsScreenProps) {
  const {
    initialListings,
    boostedListings,
    totalPets,
    initialFilter,
    loadingInitial = false,
    refreshing = false,
    onRefresh,
    header,
    title,
    subtitle,
    onSelectBreed,
    onSelectBreedQuery,
    onClearBreedSelection,
    queryPathname,
    queryDefaults,
    querySyncEnabled = true,
    onQueryChange,
    initialBreeds,
    showAds = true,
    noResultsTitle,
    noResultsSubtitle,
    fallbackListings = [],
    fallbackTitle,
    fallbackSubtitle,
  } = props;

  const { t } = useTranslation('screens');
  const route = useRoute();
  const setFilter = useListingsFilterStore((state) => state.setFilter);
  const filter = useListingsFilterStore((state) => state.filter);
  const appliedCount = useListingsFilterStore((state) => state.appliedCount);
  const previousInitialFilterRef = useRef(initialFilter);
  const [nativeFilterOpen, setNativeFilterOpen] = useState(false);
  const shouldNavigateOnBreedSelect = !!onSelectBreed || !!onSelectBreedQuery;
  const initialQueryFilter = useMemo(
    () => getListingsFilterQuery(initialFilter, queryDefaults),
    [initialFilter, queryDefaults],
  );
  const queryFilter = useMemo(
    () => getListingsFilterQuery(filter, queryDefaults),
    [filter, queryDefaults],
  );
  // Keep native modal edits local until its close/apply action.
  const routeFilterRef = useRef(queryFilter);
  if (!nativeFilterOpen) routeFilterRef.current = queryFilter;
  const breadcrumbAnimal =
    initialFilter.animal ?? (initialBreeds?.length === 1 ? initialBreeds[0]?.animal : null);
  const buyingGuideBreadcrumb =
    breadcrumbAnimal === Animal.Dog || breadcrumbAnimal === Animal.Cat
      ? {
          text: t(
            breadcrumbAnimal === Animal.Dog
              ? 'web:layout.footer.sections.dogs.buying_a_dog'
              : 'web:layout.footer.sections.cats.buying_a_cat',
          ),
          href: route(breadcrumbAnimal === Animal.Dog ? 'buying-a-dog' : 'buying-a-cat'),
        }
      : null;
  const mergedListings = useMemo<Paginator<IndexListingItem>>(() => {
    const data =
      initialListings.current_page > initialListings.last_page
        ? []
        : [...boostedListings.map((l) => ({ ...l, boosted: true })), ...initialListings.data];

    return {
      ...initialListings,
      data: data.map((listing, index) => ({ ...listing, priorityImage: index === 0 })),
    };
  }, [boostedListings, initialListings]);
  const feedbackContext = {
    animal: filter.animal,
    results_count: totalPets,
    results_bucket:
      totalPets === 0 ? '0' : totalPets <= 5 ? '1-5' : totalPets <= 20 ? '6-20' : '20+',
    applied_filter_count: appliedCount,
    breeds_count: filter.breeds?.length ?? 0,
    has_max_price: filter.maxPrice !== null,
    has_distance: filter.maxDistanceInKm !== null,
  };

  const isLocalQueryUpdate = useDebouncedQueryFilter(
    initialQueryFilter,
    routeFilterRef.current,
    (isLocalNavigation) => {
      if (!isLocalNavigation && !nativeFilterOpen) setFilter(initialFilter);
      trackSearchResults();
    },
    {
      enabled: querySyncEnabled && (!shouldNavigateOnBreedSelect || !!queryPathname),
      pathnameOverride: queryPathname,
      navigate: onQueryChange,
      debounceMs: onQueryChange ? 200 : undefined,
    },
  );

  useEffect(() => {
    if (previousInitialFilterRef.current === initialFilter) {
      return;
    }

    previousInitialFilterRef.current = initialFilter;
    if (!isLocalQueryUpdate && !nativeFilterOpen) setFilter(initialFilter);
  }, [initialFilter, isLocalQueryUpdate, nativeFilterOpen, setFilter]);

  function trackSearchResults() {
    if (loadingInitial) {
      return;
    }

    const getResultsBucket = (count: number): string => {
      if (count === 0) return '0';
      if (count >= 1 && count <= 5) return '1-5';
      if (count >= 6 && count <= 20) return '6-20';
      return '20+';
    };

    const searchParams: Record<string, any> = {
      results_count: totalPets,
      boosted_count: boostedListings.length,
      results_bucket: getResultsBucket(totalPets),
      applied_filter_count: appliedCount,
      country: filter.country,
      breed_type: filter.breedType,
      gender: filter.gender,
      pet_age: filter.petAge,
      only_breeders: filter.onlyBreeders,
      sort_by: filter.sortBy,
      has_breeds: (filter.breeds?.length ?? 0) > 0,
      breeds_count: filter.breeds?.length ?? 0,
      has_max_price: filter.maxPrice !== null,
      has_distance: filter.maxDistanceInKm !== null,
    };

    if (filter.animal) {
      searchParams.animal = filter.animal;
    }

    analytics.event('search_results', searchParams);
  }

  const paginationHeader = (
    <>
      {buyingGuideBreadcrumb && (
        <Breadcrumb className={'mb-1.5'} items={[buyingGuideBreadcrumb, { text: title ?? null }]} />
      )}
      {header ??
        (title || subtitle ? (
          <H1
            title={title}
            subtitle={subtitle}
            marginBottom={Platform.OS !== 'web'}
            hideTitleWhenNativeHeader={true}
          />
        ) : null)}

      {initialFilter.animal === Animal.Dog && (
        <Text className={'text-brand-brown-dark/75 mt-2 md:text-lg'}>
          <Trans
            t={t}
            i18nKey={'listings.index_listings.buying_dog_guide'}
            components={{
              guide: (
                <Link
                  href={route('buying-a-dog')}
                  asInline
                  containerClassName={'underline'}
                  textClassName={'underline'}
                />
              ),
            }}
          />
        </Text>
      )}

      <ListingsWebFilterControls
        totalPets={totalPets}
        boostedCount={boostedListings.length}
        initialBreeds={initialBreeds}
        routeDefaults={queryDefaults}
        onSelectBreed={onSelectBreed}
        onSelectBreedQuery={onSelectBreedQuery}
        onClearBreedSelection={onClearBreedSelection}
        onOpenChange={Platform.OS !== 'web' ? setNativeFilterOpen : undefined}
        showResultCount={
          Platform.OS === 'web' || buildQuery(queryFilter) === buildQuery(initialQueryFilter)
        }
      />
    </>
  );

  return (
    <PageContent
      className={'flex-1 !pt-0'}
      contentClassName={'flex-1'}
      nativeFlushTop={true}
      nativeFlushBottom={true}
    >
      <Pagination
        nativeSafeTop={true}
        Header={Platform.OS !== 'web' ? () => paginationHeader : undefined}
        ListHeader={Platform.OS === 'web' ? () => paginationHeader : undefined}
        showAds={showAds}
        mobileAdInterval={2}
        gap={3}
        cols={1}
        mdCols={2}
        lgCols={4}
        mdGap={6}
        paginator={mergedListings}
        loadingInitial={loadingInitial}
        onPageChange={(page) => setFilter({ page })}
        refreshing={refreshing}
        onRefresh={onRefresh}
        noResultsTitle={noResultsTitle}
        noResultsSubtitle={noResultsSubtitle}
        renderItem={(listing) => (
          <ListingCard
            listing={listing}
            boosted={listing.boosted}
            priorityImage={listing.priorityImage}
          />
        )}
        GridInsert={
          mergedListings.current_page === 1
            ? () => (
                <>
                  <ProductFeedbackPrompt promptId={'listing_search'} context={feedbackContext} />
                  {Platform.OS === 'web' && (
                    <AdBanner className={'mt-3 md:mt-6'} placement={'content'} />
                  )}
                </>
              )
            : undefined
        }
        gridInsertAfterRows={1}
        ListFooter={() => (
          <View>
            {mergedListings.current_page === 1 && mergedListings.data.length === 0 && (
              <View className={'mb-6 mt-3 md:mt-6'}>
                <ProductFeedbackPrompt promptId={'listing_no_results'} context={feedbackContext} />
              </View>
            )}

            {fallbackListings.length > 0 && fallbackTitle && (
              <>
                <H2 title={fallbackTitle} subtitle={fallbackSubtitle} />
                <View
                  className={
                    Platform.OS === 'web'
                      ? 'flex-cols-1 md:flex-cols-2 lg:flex-cols-4 flex-row gap-3 md:gap-6'
                      : 'gap-3'
                  }
                >
                  {fallbackListings.map((listing, index) => (
                    <View key={listing.id}>
                      <ListingCard listing={listing} priorityImage={index === 0} />
                    </View>
                  ))}
                </View>
              </>
            )}

            <View
              className={
                'border-brand-brown-dark/10 bg-brand-cream mx-auto mt-6 w-full max-w-2xl rounded-lg border px-5 py-4 md:flex-row md:items-center'
              }
            >
              <View className={'mb-3 flex-1 md:mb-0 md:mr-6'}>
                <Text className={'text-brand-brown-dark text-lg font-black'}>
                  {t('listings.index_listings.saved_search_title')}
                </Text>
                <Text className={'text-brand-brown-dark/70 mt-1 text-pretty'}>
                  {t('listings.index_listings.saved_search_description')}
                </Text>
              </View>

              <SearchModal
                initialData={filter}
                source={'listings_footer'}
                resultsCount={totalPets}
                boostedCount={boostedListings.length}
                trigger={({ open }) => (
                  <Button
                    text={t('listings.index_listings.save_search')}
                    icon={falBell}
                    iconPosition={'left'}
                    color={'primary'}
                    center={true}
                    testID={'listings-save-search-footer'}
                    onPress={open}
                  />
                )}
              />
            </View>
          </View>
        )}
      />
    </PageContent>
  );
});

IndexListingsScreen.pageBuilder = new PageBuilder<IndexListingsScreenProps, ListingsFilter>()
  .translationPaths(listingPageTranslationPaths)
  .request(({ req, params }) =>
    req<{ listings: Paginator<Listing>; total_pets: number }>(apiRoute('listings.index'), {
      ...prepareDataForApi(sanitizeListingsFilterParams(params), ['breeds']),
      format: 'card',
    }).then((res) => ({
      initialListings: compactListingCardPaginator(res.listings),
      totalPets: res.total_pets,
      initialFilter: sanitizeListingsFilterParams(params),
    })),
  )
  .request(({ req, params }) =>
    req<{ boosted_listings: Listing[] }>(apiRoute('listings.boosted'), {
      ...prepareDataForApi(sanitizeListingsFilterParams(params), ['breeds']),
      format: 'card',
    }).then((res) => ({
      boostedListings: compactListingCardList(res.boosted_listings),
    })),
  );

export { IndexListingsScreen };
export { listingPageTranslationPaths };
export type { IndexListingsScreenProps };
